// Zod DTOs for the anonymous/unauthenticated curriculum-picker surface
// (AUTH-01, `docs/design/AUTH-01-registration-board-level-decision.md` §2) —
// `GET /v1/exam-boards/public` + `GET /v1/levels/public`. A DELIBERATELY
// NARROW projection (id/name/slug[/examBoardId] only — no description, no
// `logoAssetId`, no `position`, no `createdAt`) over the same rows the
// role-guarded `/v1/exam-boards`/`/v1/levels` authoring surfaces expose in
// full (`./exam-board.ts`/`./level.ts`) — see that ruling doc's "Leakage
// check" for why this whitelist is safe to serve with zero auth.
import { z } from 'zod';
import { idSchema } from './primitives';

// ─── GET /v1/exam-boards/public ─────────────────────────────────────────────

export const examBoardPublicItemSchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: z.string(),
});
export type ExamBoardPublicItem = z.infer<typeof examBoardPublicItemSchema>;

export const examBoardsPublicResponseSchema = z.object({
  items: z.array(examBoardPublicItemSchema),
});
export type ExamBoardsPublicResponse = z.infer<typeof examBoardsPublicResponseSchema>;

// ─── GET /v1/levels/public?examBoardId= ─────────────────────────────────────

export const levelPublicItemSchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: z.string(),
  examBoardId: idSchema,
});
export type LevelPublicItem = z.infer<typeof levelPublicItemSchema>;

/** `examBoardId` optional — absent -> all published levels (ruling §2: "no
 * existence oracle" also means an unknown id returns an empty `items`
 * array, not a 404 — enforced by the route/service, not this schema). */
export const levelsPublicQuerySchema = z.object({
  examBoardId: idSchema.optional(),
});
export type LevelsPublicQuery = z.infer<typeof levelsPublicQuerySchema>;

export const levelsPublicResponseSchema = z.object({
  items: z.array(levelPublicItemSchema),
});
export type LevelsPublicResponse = z.infer<typeof levelsPublicResponseSchema>;
